import type { ExportOptions } from '../../types';
import { bindModalAction, createModalOverlay } from './modalBase';

interface ExportModalOptions {
  hasSelection: boolean;
  onCopyToClipboard: (options: ExportOptions) => Promise<boolean>;
  onExport: (options: ExportOptions) => void;
}

export const showExportModal = ({
  hasSelection,
  onCopyToClipboard,
  onExport,
}: ExportModalOptions): void => {
  const { overlay, close } = createModalOverlay(`
    <div class="modal-content export-modal">
      <h3 class="modal-title">Export</h3>
      <div class="export-options">
        <div class="export-option-group">
          <label class="export-label">Format</label>
          <div class="export-format-buttons">
            <button class="export-format-btn active" data-format="png">PNG</button>
            <button class="export-format-btn" data-format="svg">SVG</button>
          </div>
        </div>
        <div class="export-option-group">
          <label class="export-label">Scale</label>
          <div class="export-scale-buttons">
            <button class="export-scale-btn active" data-scale="1">1x</button>
            <button class="export-scale-btn" data-scale="2">2x</button>
            <button class="export-scale-btn" data-scale="3">3x</button>
          </div>
        </div>
        <div class="export-option-group export-checkboxes">
          <label class="export-checkbox-label">
            <input type="checkbox" id="transparent-bg">
            <span>Transparent background</span>
          </label>
          <label class="export-checkbox-label ${hasSelection ? '' : 'disabled'}">
            <input type="checkbox" id="selection-only" ${hasSelection ? '' : 'disabled'}>
            <span>Selection only${hasSelection ? '' : ' (select elements first)'}</span>
          </label>
        </div>
      </div>
      <div class="export-status" role="status" aria-live="polite"></div>
      <div class="modal-actions">
        <button class="modal-btn modal-btn-cancel">Cancel</button>
        <button class="modal-btn modal-btn-secondary" id="copy-clipboard">Copy to Clipboard</button>
        <button class="modal-btn modal-btn-confirm">Download</button>
      </div>
    </div>
  `);

  let selectedFormat: ExportOptions['format'] = 'png';
  let selectedScale: ExportOptions['scale'] = 1;

  const formatBtns = overlay.querySelectorAll('.export-format-btn');
  formatBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      formatBtns.forEach((button) => button.classList.remove('active'));
      btn.classList.add('active');
      selectedFormat = (btn as HTMLButtonElement).dataset['format'] as ExportOptions['format'];
    });
  });

  const scaleBtns = overlay.querySelectorAll('.export-scale-btn');
  scaleBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      scaleBtns.forEach((button) => button.classList.remove('active'));
      btn.classList.add('active');
      selectedScale = parseInt((btn as HTMLButtonElement).dataset['scale'] ?? '1', 10) as ExportOptions['scale'];
    });
  });

  const getExportOptions = (): ExportOptions => {
    const transparentCheckbox = overlay.querySelector('#transparent-bg') as HTMLInputElement | null;
    const selectionOnlyCheckbox = overlay.querySelector('#selection-only') as HTMLInputElement | null;

    return {
      format: selectedFormat,
      transparent: transparentCheckbox?.checked ?? false,
      scale: selectedScale,
      selectionOnly: selectionOnlyCheckbox?.checked ?? false,
    };
  };

  const statusEl = overlay.querySelector('.export-status');
  const setExportStatus = (message: string, state: 'success' | 'error' | 'info' | null): void => {
    if (!statusEl) return;
    statusEl.textContent = message;
    statusEl.classList.remove('success', 'error', 'info');
    if (state) {
      statusEl.classList.add(state);
    }
  };

  bindModalAction(overlay, '.modal-btn-cancel', close);

  const copyButton = overlay.querySelector('#copy-clipboard') as HTMLButtonElement | null;
  copyButton?.addEventListener('click', async () => {
    const options = getExportOptions();
    if (!copyButton) return;
    copyButton.disabled = true;
    setExportStatus('Copying to clipboard...', 'info');
    const copySuccess = await onCopyToClipboard(options);
    copyButton.disabled = false;
    if (copySuccess) {
      setExportStatus('Copied to clipboard.', 'success');
      setTimeout(close, 800);
    } else {
      setExportStatus('Copy failed. Use Download instead.', 'error');
    }
  });

  bindModalAction(overlay, '.modal-btn-confirm', () => {
    const options = getExportOptions();
    onExport(options);
    close();
  });
};
